import { useLiveQuery } from "dexie-react-hooks";
import {
  deriveTodayDigest,
  localDateInZone,
  mondayOf,
  type Day,
  type MealPlanApiItem,
  type RecipeApiItem,
  type TodayDigest,
} from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";

export interface TonightData {
  today: string; // YYYY-MM-DD household-local
  weekStart: string; // Monday of `today`
  plan: MealPlanApiItem | null; // this week's plan (null = none yet)
  digest: TodayDigest | null; // deriveTodayDigest output (null when no plan)
  todayDay: Day | null; // raw Day for today (digest hides skipped/empty; UI needs them)
  recipesById: Map<string, RecipeApiItem>;
  activeShoppingListId: string | null; // most recently updated non-archived list
  loading: boolean;
}

/**
 * Single source of truth for the Tonight surface. Composes entirely from Dexie
 * (no network call) so it stays in lockstep with SyncService's background sync.
 * Reuses `deriveTodayDigest` — the same derivation the digest email uses — rather
 * than re-deriving today's meals/tasks here.
 */
export function useTonight(): TonightData {
  const tz = useHouseholdTimezone();
  const today = localDateInZone(tz, new Date());
  const weekStart = mondayOf(today);

  // `db.mealPlans.get()` legitimately resolves to `undefined` when no row exists for
  // the week, which is indistinguishable from useLiveQuery's own pre-first-resolve
  // `undefined` — so wrap the row in an object to tell "resolved to no row" apart
  // from "hasn't resolved yet".
  const planResult = useLiveQuery(async () => ({ row: (await db.mealPlans.get(weekStart)) ?? null }), [weekStart]);
  const recipes = useLiveQuery(() => db.recipes.toArray(), []);
  const lists = useLiveQuery(() => db.shoppingLists.toArray(), []);

  const loading = planResult === undefined || recipes === undefined || lists === undefined;

  const planRow = planResult?.row ?? null;
  const plan = planRow && !planRow.deletedAt ? planRow : null;
  const digest = plan ? deriveTodayDigest(plan, today) : null;
  const todayDay = plan?.days.find((d) => d.date === today) ?? null;

  const recipesById = new Map<string, RecipeApiItem>();
  for (const r of recipes ?? []) recipesById.set(r.id, r);

  const activeShoppingListId =
    (lists ?? [])
      .filter((l) => !l.deletedAt && l.status === "active")
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0))[0]?.id ?? null;

  return { today, weekStart, plan, digest, todayDay, recipesById, activeShoppingListId, loading };
}
